import { useState } from 'react';
import { Heart, Clock, MapPin, Users, CheckCircle, Send, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { volunteerOpportunities } from '../mock';
import { useToast } from '../hooks/use-toast';

export default function Volunteer() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    volunteerType: '',
    availability: '',
    experience: '',
    message: ''
  });
  
  const { toast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Application Submitted!",
      description: "Thank you for your interest in volunteering. We'll contact you within 2-3 business days.",
    });
    
    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      volunteerType: '',
      availability: '',
      experience: '',
      message: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const benefits = [
    "Make a direct impact in your community",
    "Connect with like-minded individuals",
    "Flexible scheduling to fit your life",
    "Volunteer appreciation events",
    "Personal and spiritual growth opportunities",
    "Training and support provided"
  ];

  const requirements = [
    "Heart for serving others with compassion",
    "Reliable and committed to scheduled times", 
    "Able to work respectfully with diverse communities",
    "Background check for certain positions",
    "Attend brief volunteer orientation session"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-orange-100 text-orange-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Users className="h-4 w-4 mr-2" />
              Join Our Mission
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Volunteer with Purpose
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Do you have the passion and calling to serve the underserved community and share the love of God? 
              Join our team to fulfill your calling and make a lasting difference.
            </p>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Volunteer Opportunities
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find the perfect way to use your gifts and talents to serve our community
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {volunteerOpportunities.map((opportunity) => (
              <Card key={opportunity.id} className="border border-gray-100 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white">
                <CardContent className="p-8">
                  <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Heart className="h-8 w-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{opportunity.title}</h3>
                  
                  <div className="flex items-center justify-center text-orange-600 mb-4">
                    <Clock className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">{opportunity.commitment}</span>
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed text-center">{opportunity.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits and Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Heart className="h-6 w-6 text-orange-600 mr-3" />
                  Benefits of Volunteering
                </h3>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Users className="h-6 w-6 text-orange-600 mr-3" />
                  What We're Looking For
                </h3>
                <ul className="space-y-4">
                  {requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Volunteer Application Form */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Join Our Volunteer Team
            </h2>
            <p className="text-lg text-gray-600">
              Fill out the form below to begin your volunteer journey with us
            </p>
          </div>
          
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="firstName" className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-orange-600" />
                      First Name *
                    </Label>
                    <Input 
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      required 
                      className="mt-2"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-orange-600" />
                      Last Name *
                    </Label>
                    <Input 
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required 
                      className="mt-2"
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-orange-600" />
                      Email Address *
                    </Label>
                    <Input 
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required 
                      className="mt-2"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-orange-600" />
                      Phone Number
                    </Label>
                    <Input 
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="mt-2"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="volunteerType" className="flex items-center">
                      <Heart className="h-4 w-4 mr-2 text-orange-600" />
                      Volunteer Interest *
                    </Label>
                    <Select value={formData.volunteerType} onValueChange={(value) => handleChange('volunteerType', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select volunteer type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="distribution">Distribution Volunteer</SelectItem>
                        <SelectItem value="organizer">Supply Organizer</SelectItem>
                        <SelectItem value="coordinator">Outreach Coordinator</SelectItem>
                        <SelectItem value="general">General Volunteer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="availability" className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-orange-600" />
                      Availability
                    </Label>
                    <Select value={formData.availability} onValueChange={(value) => handleChange('availability', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select availability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekdays">Weekdays</SelectItem>
                        <SelectItem value="weekends">Weekends</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                        <SelectItem value="events">Events Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="experience" className="flex items-center">
                    <Users className="h-4 w-4 mr-2 text-orange-600" />
                    Previous Volunteer Experience
                  </Label>
                  <Textarea 
                    id="experience"
                    value={formData.experience}
                    onChange={(e) => handleChange('experience', e.target.value)}
                    className="mt-2 min-h-[100px]"
                    placeholder="Tell us about any previous volunteer experience or relevant skills..."
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="flex items-center">
                    <MessageSquare className="h-4 w-4 mr-2 text-orange-600" />
                    Additional Message
                  </Label>
                  <Textarea 
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="mt-2 min-h-[100px]"
                    placeholder="Why do you want to volunteer with The Blanket Project? How do you hope to contribute?"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 text-lg font-semibold transition-all duration-300 transform hover:scale-105"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            What Happens Next?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="font-semibold text-gray-900 mb-2">Application Review</h3>
              <p className="text-gray-600 text-sm">We'll review your application within 2-3 business days</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="font-semibold text-gray-900 mb-2">Orientation</h3>
              <p className="text-gray-600 text-sm">Attend a brief orientation to learn about our mission and processes</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="font-semibold text-gray-900 mb-2">Start Serving</h3>
              <p className="text-gray-600 text-sm">Begin making a difference in your community!</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}